import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { RiAddLine, RiArrowLeftLine, RiRefreshLine } from '@remixicon/react';

import { Badge } from '@/design-system/badge';
import { Button } from '@/design-system/button';
import { Card } from '@/design-system/card';
import {
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateTitle,
} from '@/design-system/empty-state';
import { Field } from '@/design-system/field';
import { FormModal } from '@/design-system/form-modal';
import { Hint } from '@/design-system/hint';
import { Input, InputRoot } from '@/design-system/input';
import { Label } from '@/design-system/label';
import { SearchSelect } from '@/design-system/search-select';
import {
  SegmentedControl,
  SegmentedItem,
} from '@/design-system/segmented-control';
import { Switch } from '@/design-system/switch';
import { TableData, type TableDataColumn } from '@/design-system/table-data';
import { Textarea } from '@/design-system/textarea';
import { toast } from '@/design-system/toaster';
import { isUjpApprover } from '@/config/ujp-permissions';
import { useAuth } from '@/contexts/AuthContext';
import { useUrlParam } from '@/hooks/urlState';
import { useProvidersDropdown } from '@/pages/shipments/components/useProvidersDropdown';
import {
  HARI_CODES,
  SHIFT_CODES,
  TariffApiError,
  tariffsAPI,
  TARIFF_TIPES,
  type TariffConfig,
  type TariffRing,
  type TariffTipe,
} from '@/services/api/tariffs';

import MoneyInput from './components/MoneyInput';
import {
  BodyRateEditor,
  CodeChips,
  SharesFields,
  TierEditor,
} from './components/TariffEditors';
import {
  blankConfig,
  blankRing,
  countOverrides,
  hasOperatingWindow,
  toCreateRingPayload,
  toCreateTariffPayload,
  validateConfigDraft,
  validateRingDraft,
  type ConfigDraft,
  type RingDraft,
} from './components/tariffForm';
import { UJP_COPY } from './copy';
import { formatRupiah, toNum } from './format';

/**
 * `/ujp/tariffs` (CR-4 / CR-4b) — the revenue side: a client's tariff config
 * (FLAT / FIXED / PER_RING / DISTANCE_TIER, dated, optionally limited to some
 * days or shifts) and, for PER_RING, its rings. Per-body overrides and
 * driver/helper shares live on FLAT configs and on rings. Approver-gated: the
 * server enforces the same 403 on writes, this is the affordance.
 */

const t = UJP_COPY.tariffs;
const TIPE_LABEL: Record<TariffTipe, string> = t.tipe;

const eyebrow =
  'text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600';

/** `driver Rp 150.000 · helper Rp 50.000`, or null when both shares are 0. */
const sharesSummary = (
  driver: string | undefined,
  helper: string | undefined
): string | null => {
  if (toNum(driver) <= 0 && toNum(helper) <= 0) return null;
  return t.sharesSummary(formatRupiah(driver ?? '0'), formatRupiah(helper ?? '0'));
};

const UjpTariffsPage: React.FC = () => {
  const { user } = useAuth();
  const canEdit = isUjpApprover(user?.email);

  const clients = useProvidersDropdown(true);
  const [clientId, setClientId] = useUrlParam<string>('clientId', '');
  const numericClientId = useMemo(() => {
    const n = Number(clientId);
    return clientId && Number.isFinite(n) && n > 0 ? n : null;
  }, [clientId]);

  const [configs, setConfigs] = useState<TariffConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [configDraft, setConfigDraft] = useState<ConfigDraft>(blankConfig);
  const [configError, setConfigError] = useState<string | null>(null);
  const [savingConfig, setSavingConfig] = useState(false);

  const [ringsFor, setRingsFor] = useState<TariffConfig | null>(null);
  const [rings, setRings] = useState<TariffRing[]>([]);
  const [ringsLoading, setRingsLoading] = useState(false);
  const [ringDraft, setRingDraft] = useState<RingDraft>(blankRing);
  const [ringError, setRingError] = useState<string | null>(null);
  const [savingRing, setSavingRing] = useState(false);
  const [togglingRingId, setTogglingRingId] = useState<string | null>(null);

  const patchConfig = (patch: Partial<ConfigDraft>) => {
    setConfigError(null);
    setConfigDraft((d) => ({ ...d, ...patch }));
  };
  const patchRing = (patch: Partial<RingDraft>) => {
    setRingError(null);
    setRingDraft((d) => ({ ...d, ...patch }));
  };

  const fetchConfigs = useCallback(async () => {
    if (numericClientId == null) {
      setConfigs([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setConfigs(await tariffsAPI.list({ clientId: numericClientId }));
    } catch (err) {
      setConfigs([]);
      setError(err instanceof Error ? err.message : 'Gagal memuat tarif');
    } finally {
      setLoading(false);
    }
  }, [numericClientId]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const saveConfig = async () => {
    if (numericClientId == null) return;
    const invalid = validateConfigDraft(configDraft);
    if (invalid) {
      setConfigError(invalid);
      return;
    }
    setSavingConfig(true);
    try {
      await tariffsAPI.create(toCreateTariffPayload(configDraft, numericClientId));
      toast.success('Tarif dibuat');
      setCreateOpen(false);
      setConfigDraft(blankConfig());
      fetchConfigs();
    } catch (err) {
      // 409 TARIFF_OVERLAP: the server names the clashing config — that
      // message IS the guidance, so it leads the toast instead of a generic line.
      const overlap =
        err instanceof TariffApiError &&
        (err.code === 'TARIFF_OVERLAP' || err.status === 409);
      toast.error(overlap ? t.overlapTitle : t.saveFailed, {
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setSavingConfig(false);
    }
  };

  /** Ring toggle (TODO-22): `PATCH /v1/tariffs/rings/:id { active }`. */
  const toggleRingActive = async (ring: TariffRing) => {
    setTogglingRingId(ring.id);
    try {
      const updated = await tariffsAPI.updateRing(ring.id, { active: !ring.active });
      const nextActive = updated?.active ?? !ring.active;
      setRings((prev) =>
        prev.map((r) => (r.id === ring.id ? { ...r, ...updated, active: nextActive } : r))
      );
      toast.success(
        nextActive ? t.ringActivated(ring.nama) : t.ringDeactivated(ring.nama)
      );
    } catch (err) {
      toast.error(t.ringToggleFailed, {
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setTogglingRingId(null);
    }
  };

  const toggleActive = async (cfg: TariffConfig) => {
    try {
      await tariffsAPI.update(cfg.id, { active: !cfg.active });
      toast.success(cfg.active ? 'Tarif dinonaktifkan' : 'Tarif diaktifkan');
      fetchConfigs();
    } catch (err) {
      toast.error('Gagal memperbarui tarif', {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  const openRings = async (cfg: TariffConfig) => {
    setRingsFor(cfg);
    setRingDraft(blankRing());
    setRingError(null);
    setRingsLoading(true);
    try {
      setRings(cfg.rings ?? (await tariffsAPI.listRings(cfg.id)));
    } catch {
      setRings([]);
    } finally {
      setRingsLoading(false);
    }
  };

  const addRing = async () => {
    if (!ringsFor || !ringDraft.nama.trim()) return;
    const invalid = validateRingDraft(ringDraft);
    if (invalid) {
      setRingError(invalid);
      return;
    }
    setSavingRing(true);
    try {
      const ring = await tariffsAPI.createRing(
        ringsFor.id,
        toCreateRingPayload(ringDraft)
      );
      setRings((prev) => [...prev, ring]);
      setRingDraft(blankRing());
      toast.success('Ring ditambah');
    } catch (err) {
      toast.error('Gagal menambah ring', {
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setSavingRing(false);
    }
  };

  const rateSummary = (cfg: TariffConfig): string => {
    if (cfg.tipe === 'FIXED') return formatRupiah(cfg.rateFixed);
    if (cfg.tipe === 'PER_RING') return `${(cfg.rings?.length ?? 0) || '—'} ring`;
    if (cfg.tipe === 'DISTANCE_TIER') {
      return t.tiersCount(cfg.distanceTiers?.length ?? 0);
    }
    return `${formatRupiah(cfg.rateFlat)} / ${formatRupiah(cfg.rateFlatMulti)}`;
  };

  const columns: TableDataColumn<TariffConfig>[] = [
    {
      key: 'tipe',
      title: 'Tipe',
      render: (_v, r) => (
        <span className="text-[13px] font-semibold text-text-strong-950">
          {TIPE_LABEL[r.tipe]}
        </span>
      ),
    },
    {
      key: 'rate',
      title: 'Rate (single / multi)',
      align: 'right',
      render: (_v, r) => {
        const overrides = r.tipe === 'PER_TRIP_FLAT' ? countOverrides(r) : 0;
        return (
          <div className="flex flex-col items-end gap-0.5">
            <span className="font-mono text-[12px] tabular-nums text-text-strong-950 whitespace-nowrap">
              {rateSummary(r)}
            </span>
            {overrides > 0 && (
              <span className="text-[11px] text-text-soft-400">
                {t.overridesCount(overrides)}
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'asuransi',
      title: 'Asuransi',
      align: 'right',
      render: (_v, r) => (
        <span className="font-mono text-[12px] tabular-nums text-text-sub-600">
          {formatRupiah(r.asuransi)}
        </span>
      ),
    },
    {
      key: 'berlaku',
      title: 'Berlaku',
      render: (_v, r) => (
        <div className="flex flex-col items-start gap-1">
          <span className="text-[12px] tabular-nums text-text-sub-600 whitespace-nowrap">
            {r.berlakuMulai}
            {r.berlakuSampai ? ` – ${r.berlakuSampai}` : ' –'}
          </span>
          {hasOperatingWindow(r) && (
            <span className="flex flex-wrap gap-1">
              {(r.hariOperasi?.length ?? 0) > 0 && (
                <Badge status="information" size="sm">
                  {r.hariOperasi.map((d) => t.hari[d]).join(' ')}
                </Badge>
              )}
              {(r.shiftOperasi?.length ?? 0) > 0 && (
                <Badge status="information" size="sm">
                  {r.shiftOperasi.map((s) => t.shift[s]).join(' · ')}
                </Badge>
              )}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'modeSupported',
      title: 'Status',
      render: (_v, r) => (
        <div className="flex flex-col items-start gap-1">
          <Badge status={r.active ? 'success' : 'neutral'} size="sm">
            {r.active ? 'Aktif' : 'Nonaktif'}
          </Badge>
          {/* Legacy flag (CR4b-D8): only an explicit false means anything. */}
          {r.modeSupported === false && (
            <Badge status="warning" size="sm">
              {t.modeUnsupported}
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      title: '',
      align: 'right',
      render: (_v, r) => (
        <div className="inline-flex items-center justify-end gap-1">
          {r.tipe === 'PER_RING' && (
            <Button
              tone="neutral"
              style="stroke"
              size="xs"
              onClick={() => openRings(r)}
            >
              Kelola ring
            </Button>
          )}
          {canEdit && (
            <Button
              tone="neutral"
              style="ghost"
              size="xs"
              onClick={() => toggleActive(r)}
            >
              {r.active ? 'Nonaktifkan' : 'Aktifkan'}
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-5 p-6">
      <div className="flex flex-col gap-3 border-b border-stroke-soft-200 pb-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="label-eyebrow mb-2">Logistic · UJP</p>
          <h1 className="text-[28px] font-extrabold leading-none tracking-tight text-text-strong-950">
            Tarif &amp; margin
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-text-sub-600">
            Sisi revenue per client: tipe tarif (flat / fixed / per ring / tier
            jarak), rate khusus per tipe unit, porsi driver, asuransi, surcharge
            multidrop, dan hari/shift berlaku. Dipakai untuk menghitung margin
            saat UJP disetujui.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button tone="neutral" style="stroke" onClick={fetchConfigs}>
            <RiRefreshLine />
            Muat ulang
          </Button>
          <Button tone="neutral" style="ghost" asChild>
            <Link to="/ujp">
              <RiArrowLeftLine />
              Kembali ke UJP
            </Link>
          </Button>
          {canEdit && (
            <Button
              onClick={() => {
                setConfigDraft(blankConfig());
                setConfigError(null);
                setCreateOpen(true);
              }}
              disabled={numericClientId == null}
            >
              <RiAddLine />
              Buat tarif
            </Button>
          )}
        </div>
      </div>

      {!canEdit && (
        <Hint>Hanya approver UJP yang dapat mengubah tarif ini.</Hint>
      )}

      <Card padding="none">
        <div className="border-b border-stroke-soft-200 px-4 py-3">
          <div className="w-64">
            <SearchSelect
              options={clients.options.map((p) => ({
                value: String(p.id),
                label: p.name,
              }))}
              value={clientId || undefined}
              selectedLabelFallback={
                clientId ? clients.labelFor(clientId) : undefined
              }
              onChange={(v) => setClientId(v ? String(v) : '')}
              placeholder="Pilih client"
              emptyMessage={clients.error ?? 'Client tidak ditemukan'}
              loading={clients.loading}
              hasMore={clients.hasMore}
              disableLocalFilter
              onSearchChange={clients.setSearch}
              onLoadMore={clients.loadMore}
            />
          </div>
        </div>

        <div className="p-4">
          <TableData<TariffConfig>
            data={error ? [] : configs}
            columns={columns}
            loading={loading}
            size="sm"
            emptyState={
              error ? (
                <EmptyState size="sm">
                  <EmptyStateTitle>Gagal memuat tarif</EmptyStateTitle>
                  <EmptyStateDescription>{error}</EmptyStateDescription>
                  <EmptyStateActions>
                    <Button
                      tone="neutral"
                      style="ghost"
                      size="sm"
                      onClick={fetchConfigs}
                    >
                      Coba lagi
                    </Button>
                  </EmptyStateActions>
                </EmptyState>
              ) : numericClientId == null ? (
                <EmptyState size="sm">
                  <EmptyStateTitle>Pilih client dulu</EmptyStateTitle>
                  <EmptyStateDescription>
                    Tarif diatur per client.
                  </EmptyStateDescription>
                </EmptyState>
              ) : (
                <EmptyState size="sm">
                  <EmptyStateTitle>Belum ada tarif untuk client ini</EmptyStateTitle>
                </EmptyState>
              )
            }
          />
        </div>
      </Card>

      {/* Create-config modal */}
      <FormModal
        open={createOpen}
        onClose={savingConfig ? () => undefined : () => setCreateOpen(false)}
        icon={<RiAddLine />}
        title="Buat tarif"
        subtitle="Tarif baru untuk client terpilih"
        size="lg"
        footer={
          <div className="flex items-center justify-between gap-3">
            <span className="min-h-4 text-[12px] text-error-base" role="alert">
              {configError ?? ''}
            </span>
            <div className="flex items-center gap-2">
              <Button
                tone="neutral"
                style="stroke"
                onClick={() => setCreateOpen(false)}
                disabled={savingConfig}
              >
                Batal
              </Button>
              <Button onClick={saveConfig} loading={savingConfig}>
                Simpan
              </Button>
            </div>
          </div>
        }
      >
        <div className="space-y-5">
          <Field>
            <Label>Tipe tarif</Label>
            <SegmentedControl
              value={configDraft.tipe}
              onValueChange={(v: string) =>
                v && patchConfig({ tipe: v as TariffTipe })
              }
              className="w-full [&>*]:flex-1"
            >
              {TARIFF_TIPES.map((tipe) => (
                <SegmentedItem key={tipe} value={tipe}>
                  {TIPE_LABEL[tipe]}
                </SegmentedItem>
              ))}
            </SegmentedControl>
          </Field>

          {configDraft.tipe === 'PER_TRIP_FLAT' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Field>
                  <Label htmlFor="tariff-flat">{t.rateSingle}</Label>
                  <MoneyInput
                    id="tariff-flat"
                    aria-label={t.rateSingle}
                    value={configDraft.rateFlat}
                    onChange={(v) => patchConfig({ rateFlat: v })}
                  />
                </Field>
                <Field>
                  <Label htmlFor="tariff-flat-multi">{t.rateMulti}</Label>
                  <MoneyInput
                    id="tariff-flat-multi"
                    aria-label={t.rateMulti}
                    value={configDraft.rateFlatMulti}
                    onChange={(v) => patchConfig({ rateFlatMulti: v })}
                  />
                </Field>
              </div>
              <BodyRateEditor
                idPrefix="flat-body"
                label={t.bodyOverridesSingle}
                rows={configDraft.ratesFlatPerBody}
                onChange={(rows) => patchConfig({ ratesFlatPerBody: rows })}
                disabled={savingConfig}
              />
              <BodyRateEditor
                idPrefix="flat-body-multi"
                label={t.bodyOverridesMulti}
                rows={configDraft.ratesFlatPerBodyMulti}
                onChange={(rows) => patchConfig({ ratesFlatPerBodyMulti: rows })}
                disabled={savingConfig}
                hint={t.bodyHint}
              />
              <SharesFields
                idPrefix="flat"
                value={configDraft.shares}
                onChange={(shares) => patchConfig({ shares })}
                disabled={savingConfig}
              />
            </div>
          )}

          {configDraft.tipe === 'FIXED' && (
            <Field>
              <Label htmlFor="tariff-fixed">{t.rateFixed}</Label>
              <MoneyInput
                id="tariff-fixed"
                aria-label={t.rateFixed}
                value={configDraft.rateFixed}
                onChange={(v) => patchConfig({ rateFixed: v })}
              />
            </Field>
          )}

          {configDraft.tipe === 'PER_RING' && <Hint>{t.perRingHint}</Hint>}

          {configDraft.tipe === 'DISTANCE_TIER' && (
            <TierEditor
              rows={configDraft.distanceTiers}
              onChange={(rows) => patchConfig({ distanceTiers: rows })}
              disabled={savingConfig}
            />
          )}

          {/* Common to every tipe */}
          <div className="grid grid-cols-2 gap-3">
            <Field>
              <Label htmlFor="tariff-asuransi">{t.asuransi}</Label>
              <MoneyInput
                id="tariff-asuransi"
                aria-label={t.asuransi}
                value={configDraft.asuransi}
                onChange={(v) => patchConfig({ asuransi: v })}
              />
            </Field>
            <Field>
              <Label htmlFor="tariff-reverse">{t.hargaReverse}</Label>
              <MoneyInput
                id="tariff-reverse"
                aria-label={t.hargaReverse}
                value={configDraft.hargaReverse}
                onChange={(v) => patchConfig({ hargaReverse: v })}
              />
              <Hint>{t.hargaReverseHint}</Hint>
            </Field>
          </div>

          <div className="space-y-2">
            <p className={eyebrow}>{t.multidrop}</p>
            <div className="grid grid-cols-2 gap-3">
              <Field>
                <Label htmlFor="tariff-free-points">{t.multidropFreePoints}</Label>
                <InputRoot size="md">
                  <Input
                    id="tariff-free-points"
                    type="number"
                    inputMode="numeric"
                    min={0}
                    step={1}
                    value={configDraft.multidropFreePoints}
                    className="min-w-0 w-full font-mono tabular-nums text-right"
                    onChange={(e) =>
                      patchConfig({ multidropFreePoints: e.target.value })
                    }
                  />
                </InputRoot>
              </Field>
              <Field>
                <Label htmlFor="tariff-extra-rate">{t.multidropExtraRate}</Label>
                <MoneyInput
                  id="tariff-extra-rate"
                  aria-label={t.multidropExtraRate}
                  value={configDraft.multidropExtraRate}
                  onChange={(v) => patchConfig({ multidropExtraRate: v })}
                />
              </Field>
            </div>
            <Hint>{t.multidropHint}</Hint>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <CodeChips
              label={t.hariOperasi}
              options={HARI_CODES}
              labels={t.hari}
              value={configDraft.hariOperasi}
              onChange={(hariOperasi) => patchConfig({ hariOperasi })}
              emptyLabel={t.everyDay}
              disabled={savingConfig}
            />
            <CodeChips
              label={t.shiftOperasi}
              options={SHIFT_CODES}
              labels={t.shift}
              value={configDraft.shiftOperasi}
              onChange={(shiftOperasi) => patchConfig({ shiftOperasi })}
              emptyLabel={t.everyShift}
              disabled={savingConfig}
            />
          </div>

          <div className="flex items-start justify-between gap-3 rounded-gsm border border-stroke-soft-200 bg-bg-weak-50 p-3">
            <div>
              <Label htmlFor="tariff-tagih-per-rit">{t.tagihPerRit}</Label>
              <p className="mt-0.5 text-[11px] text-text-soft-400">
                {t.tagihPerRitHint}
              </p>
            </div>
            <Switch
              id="tariff-tagih-per-rit"
              aria-label={t.tagihPerRit}
              checked={configDraft.tagihPerRit}
              disabled={savingConfig}
              onCheckedChange={(tagihPerRit) => patchConfig({ tagihPerRit })}
            />
          </div>

          <Field>
            <Label htmlFor="tariff-catatan">{t.catatan}</Label>
            <Textarea
              id="tariff-catatan"
              rows={2}
              maxLength={1000}
              value={configDraft.catatan}
              placeholder={t.catatanPlaceholder}
              disabled={savingConfig}
              className="resize-none"
              onChange={(e) => patchConfig({ catatan: e.target.value })}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field>
              <Label htmlFor="tariff-berlaku">{t.berlakuMulai}</Label>
              <InputRoot size="md">
                <Input
                  className="min-w-0 w-full"
                  id="tariff-berlaku"
                  type="date"
                  value={configDraft.berlakuMulai}
                  onChange={(e) => patchConfig({ berlakuMulai: e.target.value })}
                />
              </InputRoot>
              <Hint>{t.berlakuMulaiHint}</Hint>
            </Field>
            <Field>
              <Label htmlFor="tariff-berlaku-sampai">{t.berlakuSampai}</Label>
              <InputRoot size="md">
                <Input
                  className="min-w-0 w-full"
                  id="tariff-berlaku-sampai"
                  type="date"
                  value={configDraft.berlakuSampai}
                  min={configDraft.berlakuMulai || undefined}
                  onChange={(e) => patchConfig({ berlakuSampai: e.target.value })}
                />
              </InputRoot>
              <Hint>{t.berlakuSampaiHint}</Hint>
            </Field>
          </div>
        </div>
      </FormModal>

      {/* Rings modal */}
      <FormModal
        open={!!ringsFor}
        onClose={savingRing ? () => undefined : () => setRingsFor(null)}
        icon={<RiAddLine />}
        title="Ring tarif"
        subtitle={ringsFor ? TIPE_LABEL[ringsFor.tipe] : ''}
        size="lg"
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button
              tone="neutral"
              style="stroke"
              onClick={() => setRingsFor(null)}
            >
              Tutup
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {ringsLoading ? (
            <p className="text-[13px] text-text-sub-600">Memuat ring…</p>
          ) : rings.length === 0 ? (
            <p className="text-[13px] text-text-sub-600">Belum ada ring.</p>
          ) : (
            <ul className="divide-y divide-stroke-soft-200 rounded-gsm border border-stroke-soft-200">
              {rings
                .slice()
                .sort((a, b) => a.urutan - b.urutan)
                .map((r) => {
                  const overrides = countOverrides(r);
                  const shares = sharesSummary(r.driverShare, r.helperShare);
                  const meta = [
                    overrides > 0 ? t.overridesCount(overrides) : null,
                    shares,
                    r.keterangan?.trim() || null,
                  ].filter((m): m is string => !!m);
                  const inactive = r.active === false;
                  return (
                    <li
                      key={r.id}
                      data-testid={`ring-row-${r.id}`}
                      data-inactive={inactive || undefined}
                      className={`flex items-start justify-between gap-3 px-3 py-2 text-[13px] ${
                        inactive ? 'text-text-disabled-300' : ''
                      }`}
                    >
                      <span
                        className={`min-w-0 ${
                          inactive ? 'text-text-soft-400' : 'text-text-strong-950'
                        }`}
                      >
                        <span className="font-mono text-text-soft-400 mr-2">
                          {r.urutan}
                        </span>
                        {r.nama}
                        {inactive && (
                          <Badge status="neutral" size="sm" className="ml-2">
                            {t.ringInactive}
                          </Badge>
                        )}
                        {meta.length > 0 && (
                          <span className="block text-[11px] text-text-soft-400">
                            {meta.join(' · ')}
                          </span>
                        )}
                      </span>
                      <span className="flex shrink-0 items-center gap-2">
                        <span
                          className={`font-mono tabular-nums ${
                            inactive ? 'text-text-soft-400' : 'text-text-sub-600'
                          }`}
                        >
                          {formatRupiah(r.ratePerTrip)} / {formatRupiah(r.ratePerTripMulti)}
                        </span>
                        {canEdit && (
                          <Button
                            tone="neutral"
                            style="ghost"
                            size="xs"
                            aria-label={`${inactive ? t.ringActivate : t.ringDeactivate} ring ${r.nama}`}
                            loading={togglingRingId === r.id}
                            disabled={togglingRingId != null}
                            onClick={() => toggleRingActive(r)}
                          >
                            {inactive ? t.ringActivate : t.ringDeactivate}
                          </Button>
                        )}
                      </span>
                    </li>
                  );
                })}
            </ul>
          )}

          {canEdit && (
            <div className="space-y-4 rounded-gsm border border-stroke-soft-200 bg-bg-weak-50 p-3">
              <p className={eyebrow}>Tambah ring</p>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Field>
                  <Label htmlFor="ring-nama">Nama</Label>
                  <InputRoot size="sm">
                    <Input
                      className="min-w-0 w-full"
                      id="ring-nama"
                      value={ringDraft.nama}
                      placeholder="mis. Karawang"
                      onChange={(e) => patchRing({ nama: e.target.value })}
                    />
                  </InputRoot>
                </Field>
                <Field>
                  <Label htmlFor="ring-urutan">Urutan</Label>
                  <InputRoot size="sm">
                    <Input
                      className="min-w-0 w-full"
                      id="ring-urutan"
                      type="number"
                      value={ringDraft.urutan}
                      onChange={(e) => patchRing({ urutan: e.target.value })}
                    />
                  </InputRoot>
                </Field>
                <Field>
                  <Label htmlFor="ring-single">Single</Label>
                  <MoneyInput
                    id="ring-single"
                    aria-label="Rate single ring"
                    size="sm"
                    value={ringDraft.ratePerTrip}
                    onChange={(v) => patchRing({ ratePerTrip: v })}
                  />
                </Field>
                <Field>
                  <Label htmlFor="ring-multi">Multi</Label>
                  <MoneyInput
                    id="ring-multi"
                    aria-label="Rate multi ring"
                    size="sm"
                    value={ringDraft.ratePerTripMulti}
                    onChange={(v) => patchRing({ ratePerTripMulti: v })}
                  />
                </Field>
              </div>
              <BodyRateEditor
                idPrefix="ring-body"
                label={t.bodyOverridesSingle}
                rows={ringDraft.ratesPerBody}
                onChange={(rows) => patchRing({ ratesPerBody: rows })}
                disabled={savingRing}
              />
              <BodyRateEditor
                idPrefix="ring-body-multi"
                label={t.bodyOverridesMulti}
                rows={ringDraft.ratesPerBodyMulti}
                onChange={(rows) => patchRing({ ratesPerBodyMulti: rows })}
                disabled={savingRing}
                hint={t.bodyHint}
              />
              <SharesFields
                idPrefix="ring"
                size="sm"
                value={ringDraft.shares}
                onChange={(shares) => patchRing({ shares })}
                disabled={savingRing}
              />
              <Field>
                <Label htmlFor="ring-keterangan">{t.keterangan}</Label>
                <InputRoot size="sm">
                  <Input
                    className="min-w-0 w-full"
                    id="ring-keterangan"
                    value={ringDraft.keterangan}
                    placeholder={t.keteranganPlaceholder}
                    maxLength={500}
                    onChange={(e) => patchRing({ keterangan: e.target.value })}
                  />
                </InputRoot>
              </Field>
              <div className="flex items-center justify-between gap-3">
                <span className="min-h-4 text-[12px] text-error-base" role="alert">
                  {ringError ?? ''}
                </span>
                <Button
                  size="sm"
                  onClick={addRing}
                  loading={savingRing}
                  disabled={!ringDraft.nama.trim()}
                >
                  <RiAddLine />
                  Tambah ring
                </Button>
              </div>
            </div>
          )}
        </div>
      </FormModal>
    </div>
  );
};

export default UjpTariffsPage;
